import clsx from 'clsx';
import { Imatge } from '@portal-internet/components';
import { getStorageValue, setStorageValue } from '@portal-internet/core';
import Boto from 'components/3cat/ui/Boto';
import Icona from 'components/3cat/ui/Icona';
import { useModulAvis } from 'hooks/useModulAvis';
import { useProducte } from 'hooks/3cat/useProducte';
import { useModal } from 'hooks/3cat/useModal';
import { useUser } from 'hooks/useUser';
import __isUndefined from 'lodash/isUndefined';
import { useRouter } from 'next/router';
import { useEffect, useId, useRef, useState, useMemo } from 'react';
import __isEmpty from 'lodash/isEmpty';
import Text from 'components/3cat/Text';
import styles from './onboardingbase.module.scss';

// --- HELPER D'ADOBE TARGET PER CLICS ---
const trackTargetClick = (clickMbox, botoId) => {
    const trackEvent = globalThis?.window?.adobe?.target?.trackEvent;

    if (typeof trackEvent === 'function') {
        console.log(`🎯 Adobe Target: Trackejant clic a [${clickMbox}]`);

        trackEvent({
            mbox: clickMbox,
            params: {
                buttonId: botoId,
                elementTag: 'button'
            },
            success: () => {
                console.log('✅ Adobe Target: Clic enviat correctament');
            },
            error: (status, error) => {
                console.warn('❌ Adobe Target: Error enviant el clic', status, error);
            }
        });
    } else {
        console.warn('⚠️ Adobe Target: La funció trackEvent no està disponible');
    }
};

// --- HELPER D'ADOBE TARGET ---
const fetchTargetVariant = async (mbox, fallbackVariant = 'I') => {
    const timeout = 3000;
    const interval = 100;
    const start = Date.now();
    let target = null;

    // Esperem a que window.adobe.target estigui disponible
    while (Date.now() - start < timeout) {
        target = globalThis?.window?.adobe?.target;
        if (target) break;
        await new Promise((r) => setTimeout(r, interval));
    }

    if (!target) return fallbackVariant;

    // Demanem l'oferta
    return new Promise((resolve) => {
        try {
            if (typeof target.getOffer === 'function') {
                target.getOffer({
                    mbox,
                    success: (offer) => {
                        let variantRaw = offer?.[0]?.content?.[0]?.version;
                        const variant = typeof variantRaw === 'string' ? variantRaw.trim().toUpperCase() : fallbackVariant;
                        resolve(variant);
                    },
                    error: (error) => {
                        console.warn('Error obtenint Adobe Target Offer', error);
                        resolve(fallbackVariant);
                    }
                });
            } else {
                resolve(fallbackVariant);
            }
        } catch (error) {
            console.warn('Error obtenint Adobe Target Offer', error);
            resolve(fallbackVariant);
        }
    });
};
// ------------------------------

export default function Component(props) {
    const { onboardingActiu } = props;
    const { uuid } = useUser();
    const { setModal, removeModal } = useModal();

    // ✅ Obtenir variants disponibles de la configuració
    const validSegments = useMemo(() => Object.keys(onboardingActiu?.variants || {}), [onboardingActiu?.variants]);
    const isValidSegment = (seg) => validSegments.length === 0 || validSegments.includes(seg);

    // ✅ Obtenir segment de Target o cookie
    const [segment, setSegment] = useState(onboardingActiu?.defaultSegment || 'I');
    const initialFetchDone = useRef(false);

    // ✅ Mergear: base + variant (només titol, descripcio, botons)
    const onboardingFinal = useMemo(() => {
        const base = onboardingActiu?.base || onboardingActiu || {};
        const variantOverrides = onboardingActiu?.variants?.[segment] || {};

        // Mergear només els camps modificables
        return {
            ...base,
            ...(variantOverrides?.titol && { titol: variantOverrides.titol }),
            ...(variantOverrides?.descripcio && { descripcio: variantOverrides.descripcio }),
            ...(variantOverrides?.botons && { botons: variantOverrides.botons })
        };
    }, [segment, onboardingActiu]);

    const [showOnboarding, setShowOnboarding] = useState(false);
    const [showedToday, setShowedToday] = useState(true);
    const [cookieChecked, setCookieChecked] = useState(false);
    const timeoutShowOnboarding = useRef();
    const { isWebviewActive } = useProducte();
    const { isModulAvisActive } = useModulAvis();

    const router = useRouter();
    const refOnboarding = useRef();
    const isTest =
        typeof window !== 'undefined' && window.Cypress && window?.Cypress?.spec?.baseName !== 'Onboarding.test.feature';

    const usuarisLogats = onboardingActiu?.usuarisLogats !== undefined ? onboardingActiu.usuarisLogats : true;
    const usuarisNoLogats = onboardingActiu?.usuarisNoLogats !== undefined ? onboardingActiu.usuarisNoLogats : true;
    const { signUp, signIn, loginState, loginStateTypes, isAuthenticated } = useUser();
    const nomCookie = `onboarding_3cat_${onboardingActiu?.id}`;
    const estructuraCookieDefault = {
        lastShown: null, // timestamp en ms de quan es va mostrar l'última vegada
        count: 0,
        variant: onboardingActiu?.defaultSegment || 'I'
    };
    const maxOnboardingCount = onboardingActiu.numMaxCopsQueEsMostra;
    const horasCadaQuantMs = (onboardingActiu?.horesCadaQuantEsMostra || 24) * 60 * 60 * 1000;

    const isEmbedOrPopup = router.asPath.includes('/popup/') || router.asPath.includes('/embed/');

    const saveOnStorage = (values) => {
        const expiresInDays = Math.ceil((onboardingActiu?.horesCadaQuantEsMostra || 24) / 24);
        setStorageValue(nomCookie, values, {
            path: '/',
            domain: '.3cat.cat',
            expires: expiresInDays
        });
    };
    const initializeValues = (data) => {
        let response;
        if (__isUndefined(data)) {
            response = estructuraCookieDefault;
        } else {
            response = data;
            if (__isUndefined(response.count)) {
                response.count = 0;
            }
            if (__isUndefined(response.variant)) {
                response.variant = onboardingActiu?.defaultSegment || 'I';
            }
        }
        return response;
    };

    const fetchDayCookieData = async () => {
        const data = await getStorageValue(nomCookie);
        let value = initializeValues(data);
        const nowMs = Date.now();

        // ✅ 1️⃣ PRIMER: Comprovar qualificació de l'usuari
        if (!mostrarOnboarding()) {
            console.log('⚠️ Usuari no qualifica per veure el modal');
            setCookieChecked(true);
            return;
        }

        // ✅ 2️⃣ SEGON: Comprovar si ha transcorregut temps suficient
        const tempsAssolit = value.lastShown === null || nowMs - value.lastShown >= horasCadaQuantMs;

        if (tempsAssolit && value.lastShown !== null) {
            console.log(`✅ Han passat ${onboardingActiu?.horesCadaQuantEsMostra} hores, resetejant count`);
            value.count = 0; // ← Reseteja contador
            value.lastShown = nowMs;
        } else if (value.lastShown === null) {
            console.log('✅ Primera vegada, inicialitzant timestamp');
            value.lastShown = nowMs;
        }

        // ✅ 3️⃣ TERCER: Comprovar si pot mostrar-se dins del límit de vegades
        if (value.count >= maxOnboardingCount) {
            console.log(`⚠️ Ja s'ha mostrat ${value.count}/${maxOnboardingCount} vegades en aquesta finestra`);
            setCookieChecked(true);
            return;
        }

        // ✅ 4️⃣ QUART: Si useTarget és false, sempre usar defaultSegment
        if (!onboardingActiu?.useTarget) {
            const defaultSeg = onboardingActiu?.defaultSegment || 'I';
            setSegment(defaultSeg);
            value.variant = defaultSeg;
        } else {
            // ✅ Crida a Adobe Target només si qualifica i temps permès
            if (onboardingActiu?.targetMboxName) {
                try {
                    const variant = await fetchTargetVariant(
                        onboardingActiu.targetMboxName,
                        onboardingActiu.defaultSegment || 'I'
                    );
                    const finalSegment = isValidSegment(variant) ? variant : onboardingActiu?.defaultSegment || 'I';
                    setSegment(finalSegment);
                    value.variant = finalSegment;
                } catch (error) {
                    console.warn('Error cridant Adobe Target:', error);
                    value.variant = onboardingActiu?.defaultSegment || 'I';
                }
            }
        }

        // ✅ 5️⃣ CINQUÈ: Guardar cookie i marcar que es pot mostrar
        value.count++;
        setShowedToday(false);
        saveOnStorage(value);

        setCookieChecked(true);
    };

    const tancaOnboarding = () => {
        removeModal();
        netejeUrlOnboarding();
        setShowOnboarding(false);
        setShowedToday(true);
    };

    const netejeUrlOnboarding = () => {
        let url = router.asPath?.split('#')[0];
        router.push(url, undefined, { scroll: false });
    };

    const getToday = () => {
        const dateObj = new Date();
        const month = dateObj.getUTCMonth() + 1;
        const day = dateObj.getUTCDate();
        const year = dateObj.getUTCFullYear();
        return year + '/' + month + '/' + day;
    };

    const mostrarOnboarding = () => {
        // ✅ Comprovar si l'usuari qualifica per veure el modal
        const esLogat = isAuthenticated === true;

        if (esLogat && usuarisLogats === true) {
            return true; // ✅ Logat i permès per logats
        }

        if (!esLogat && usuarisNoLogats === true) {
            return true; // ✅ No logat i permès per no logats
        }

        return false; // ❌ No qualifica
    };

    useEffect(() => {
        // ✅ SEMPRE espera loginState fins que estigui en estat definitiu (AUTHENTICATED o NOT_AUTHENTICATED)
        // NO inicializing, NO undefined - necessitem saber de forma definitiva si està logat o no
        const isLoginStateResolved = loginState === loginStateTypes.AUTHENTICATED || loginState === loginStateTypes.GUEST;

        if (!initialFetchDone.current && onboardingActiu?.actiu && isLoginStateResolved) {
            fetchDayCookieData();
            initialFetchDone.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onboardingActiu?.actiu, loginState, usuarisLogats, usuarisNoLogats, loginStateTypes]);

    // ✅ Sincronitzar la variant a la cookie quan canvia (NOMÉS si useTarget=true)
    useEffect(() => {
        if (!onboardingActiu?.useTarget) return; // No guardar variants si useTarget és false

        const updateVariantInCookie = async () => {
            const data = await getStorageValue(nomCookie);
            if (data) {
                data.variant = segment;
                const expiresInDays = Math.ceil((onboardingActiu?.horesCadaQuantEsMostra || 24) / 24);
                try {
                    setStorageValue(nomCookie, data, {
                        path: '/',
                        domain: '.3cat.cat',
                        expires: expiresInDays
                    });
                } catch (err) {
                    console.warn('Error actualitzant variant a cookie:', err);
                }
            }
        };

        updateVariantInCookie();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [segment, onboardingActiu?.useTarget]);

    useEffect(() => {
        if (!__isUndefined(refOnboarding.current) && showOnboarding === true && !isTest && !showedToday) {
            refOnboarding.current.focus();
            //document.getElementById('__mainContent').setAttribute('inert', true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refOnboarding.current]);

    useEffect(() => {
        if (mostrarOnboarding() && !isWebviewActive && cookieChecked) {
            timeoutShowOnboarding.current = setTimeout(() => {
                setShowOnboarding(true);
            }, 1500);
        } else if (!mostrarOnboarding()) {
            setShowOnboarding(false);
        }
        return () => clearTimeout(timeoutShowOnboarding.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loginState, cookieChecked]);

    // ✅ Track click events a Target (NOMÉS si useTarget=true)
    // ⚠️ NO és async, es crida sense await per fer fire-and-forget
    const trackClickToTarget = (botoId) => {
        console.log('🔍 trackClickToTarget called:', {
            botoId,
            useTarget: onboardingActiu?.useTarget,
            hasTargetMbox: !!onboardingActiu?.targetMboxNameClick
        });

        if (!onboardingActiu?.useTarget) {
            console.log('⚠️ useTarget is false, skipping tracking');
            return;
        }

        if (!onboardingActiu?.targetMboxNameClick) {
            console.log('⚠️ targetMboxNameClick not configured, skipping tracking');
            return;
        }

        try {
            const clickMbox = `${onboardingActiu.targetMboxNameClick}`;
            console.log('✅ Sending to trackTargetClick:', clickMbox);
            trackTargetClick(clickMbox, botoId);
        } catch (err) {
            console.warn(`❌ Error tracking click [${botoId}]:`, err);
        }
    };

    // ✅ Actions amb tracking (NO espera, tracking és fire-and-forget)
    const actionsWithTracking = {
        signUp: (boto) => {
            console.log('📍 Action: signUp');
            trackClickToTarget('signUp');
            signUp();
        },
        signIn: (boto) => {
            console.log('📍 Action: signIn');
            trackClickToTarget('signIn');
            signIn();
        },
        close: (boto) => {
            console.log('📍 Action: close');
            trackClickToTarget('close');
            tancaOnboarding();
        },
        default: (boto) => {
            console.log('📍 Action: default');
            trackClickToTarget('default');
            tancaOnboarding();
            // Navigate to link if available
            if (boto?.type === 'link' && boto?.link) {
                setTimeout(() => {
                    window.location.href = boto.link;
                }, 100);
            }
        }
    };

    // ✅ Handler per botons link (sense action)
    const handleBotoClick = (index) => {
        console.log('🎯 handleBotoClick called:', { index });
        const botoId = `boto_${index}`;
        trackClickToTarget(botoId);
    };

    const arrayEstils = onboardingActiu.estils?.split(' ') || [];
    const stylesOnboarding = clsx({
        [styles.onBoardingWrapper]: true,
        ...arrayEstils.reduce((acc, currentStyle) => {
            acc[styles[currentStyle]] = !__isEmpty(currentStyle);
            return acc;
        }, {})
    });

    const componentId = useId().replace(/:/g, '-');
    const containerId = `custom-container-onboarding-${componentId}`;
    const cssProcessat = onboardingActiu.css
        ? `${onboardingActiu.css.replace(/(^|[^}]+)\s*\{/g, (match) => `#${containerId} ${match.trim()}`)}`
        : '';

    const contentOnBoardingStatic = () => {
        return (
            <div
                data-testid={`onboarding-custom-${onboardingActiu.id}`}
                id={containerId}
                className={stylesOnboarding}
                {...{
                    'role': 'dialog',
                    'aria-modal': 'true',
                    'aria-labelledby': 'onboarding-titol'
                }}
            >
                {cssProcessat && <style>{cssProcessat}</style>}
                <div dangerouslySetInnerHTML={{ __html: onboardingActiu?.contingut }}></div>
            </div>
        );
    };
    const contentOnBoarding = () => {
        return (
            <div
                data-testid={`onboarding-custom-${onboardingActiu.id}`}
                id={containerId}
                className={stylesOnboarding}
                {...{
                    'role': 'dialog',
                    'aria-modal': 'true',
                    'aria-labelledby': 'onboarding-titol'
                }}
            >
                {cssProcessat && <style>{cssProcessat}</style>}
                <div className={styles.mask + ' ' + styles.onboarding} onClick={() => tancaOnboarding()} ref={refOnboarding}>
                    <div className={styles.wrapper} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.img}>
                            <div className={styles.imgTop}>
                                <Imatge
                                    src={onboardingFinal.imatge?.src}
                                    layout={onboardingFinal.imatge?.layout}
                                    objectFit={onboardingFinal.imatge?.objectFit}
                                    alt={onboardingFinal.imatge?.alt}
                                    //className={styles.imatgeModal}
                                />
                            </div>
                        </div>
                        <div className={styles.text}>
                            <div className={styles.imgBottom}>
                                <Imatge
                                    src={onboardingFinal.imatgeMobile?.src}
                                    layout={onboardingFinal.imatgeMobile?.layout}
                                    objectFit={onboardingFinal.imatgeMobile?.objectFit}
                                    alt={onboardingFinal.imatgeMobile?.alt}
                                />
                            </div>
                            <button className={styles.tanca} onClick={() => tancaOnboarding()}>
                                <Icona icona='tanca' className={styles.ico_tanca} alt='Tanca' ariaHidden={true} />
                                <span className='sr-only'>Tanca finestra modal</span>
                            </button>

                            <div className={styles.contingutMobil}>
                                <div className={styles.wrapperLogo}>
                                    <Imatge
                                        src={onboardingFinal.logo?.src}
                                        width={onboardingFinal.logo?.width}
                                        height={onboardingFinal.logo?.height}
                                        alt={onboardingFinal.logo?.alt}
                                    />
                                </div>
                                <Text tag='h1' className={styles.title} text={onboardingFinal.titol} id='onboarding-titol' />
                                <Text tag='div' className={styles.subtitle} text={onboardingFinal.descripcio} />
                                <div className={styles.btns}>
                                    {onboardingFinal.botons?.map((boto, index) => (
                                        <Boto
                                            key={`boto-onboarding-${index}`}
                                            tag={boto.action ? 'button' : boto.type === 'link' ? 'a' : 'button'}
                                            link={boto.action ? undefined : boto.link}
                                            text={boto.text}
                                            estil={boto.botoEstil}
                                            className={boto.estils
                                                ?.split(' ')
                                                .map((classe) => styles[classe] || classe)
                                                .join(' ')}
                                            onClick={(e) => {
                                                console.log('Boto clicked:', index, { type: boto.type, action: boto.action });

                                                // Si és un link sense action, deixar navegació normal
                                                if (boto.type === 'link' && !boto.action) return;

                                                // Si té action, executar-la
                                                if (boto.action) {
                                                    const action = actionsWithTracking[boto.action];
                                                    if (action) {
                                                        action(boto);
                                                    }
                                                }
                                            }}
                                            dataTestId={boto.dataTestId}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    useEffect(() => {
        if (!onboardingActiu) return;

        // ✅ Si useTarget és true i la variant és per defecte sense personalització, no obrir modal.
        if (onboardingActiu?.useTarget && segment === 'I' && __isEmpty(onboardingActiu?.variants?.I)) {
            removeModal();
            return;
        }

        if (showOnboarding && !isTest && !isEmbedOrPopup && !isModulAvisActive && !showedToday && mostrarOnboarding()) {
            setModal(onboardingActiu?.contingut ? contentOnBoardingStatic() : contentOnBoarding());
            return;
        }

        removeModal();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showOnboarding, isTest, isEmbedOrPopup, isModulAvisActive, showedToday, onboardingActiu, segment, isAuthenticated]);

    return null;
}
